import { DEFAULT_ASSET_ROPA_REVIEW_FILTERS, type AssetRopaReviewFilters } from 'config/api/assetRopa/assetRopaFilter.types';
import processing_activities_messages from 'messages/processing_activities_messages';

import {
  buildDpiaCombinedStatusDrawerSection,
  buildDpiaIndicationDrawerSection,
  readDpiaFiltersFromTableSections,
  writeDpiaFiltersToTableSections,
} from '../_shared/apaDpiaDrawerFilters';

import type { AssetRopaReviewFilterOptions } from './assetRopaReview.types';

type ReviewStatusMessageKey = keyof typeof processing_activities_messages.asset_ropa.review_status;

const REVIEW_STATUS_OPTIONS = Object.keys(processing_activities_messages.asset_ropa.review_status) as ReviewStatusMessageKey[];
const WARNING_STATE_OPTIONS = ['with_warnings', 'no_warnings'] as const;
const BOOLEAN_FILTER_OPTIONS = ['true', 'false'] as const;

export type AssetRopaReviewTableFilterChip = {
  id: string;
  name: string | string[];
};

export type AssetRopaReviewTableFilterState = {
  review_status?: AssetRopaReviewTableFilterChip[];
  asset_type?: AssetRopaReviewTableFilterChip[];
  operational_owner?: AssetRopaReviewTableFilterChip[];
  warning_state?: AssetRopaReviewTableFilterChip[];
  dpia_indication?: AssetRopaReviewTableFilterChip[];
  dpia_status?: AssetRopaReviewTableFilterChip[];
  sensitive_personal_data?: AssetRopaReviewTableFilterChip[];
  third_country_processing?: AssetRopaReviewTableFilterChip[];
};

export type AssetRopaReviewTableAvailableFilters = {
  review_status: {
    sectionName: string[];
    filters: AssetRopaReviewTableFilterChip[];
    isSingleSelect: boolean;
  };
  asset_type?: {
    sectionName: string[];
    filters: AssetRopaReviewTableFilterChip[];
    isSingleSelect: boolean;
  };
  operational_owner?: {
    sectionName: string[];
    filters: AssetRopaReviewTableFilterChip[];
    isSingleSelect: boolean;
  };
  warning_state: {
    sectionName: string[];
    filters: AssetRopaReviewTableFilterChip[];
    isSingleSelect: boolean;
  };
  dpia_indication: {
    sectionName: string[];
    filters: AssetRopaReviewTableFilterChip[];
    isSingleSelect: boolean;
  };
  dpia_status: {
    sectionName: string[];
    filters: AssetRopaReviewTableFilterChip[];
    isSingleSelect: boolean;
    isDisabled?: boolean;
    disabledTooltip?: string[];
  };
  sensitive_personal_data: {
    sectionName: string[];
    filters: AssetRopaReviewTableFilterChip[];
    isSingleSelect: boolean;
  };
  third_country_processing: {
    sectionName: string[];
    filters: AssetRopaReviewTableFilterChip[];
    isSingleSelect: boolean;
  };
};

const reviewStatusMessage = (value: ReviewStatusMessageKey): string | string[] =>
  processing_activities_messages.asset_ropa.review_status[value];

const warningStateMessage = (value: typeof WARNING_STATE_OPTIONS[number]): string | string[] =>
  processing_activities_messages.asset_ropa.filters[value];

const booleanFilterMessage = (value: typeof BOOLEAN_FILTER_OPTIONS[number]): string | string[] =>
  processing_activities_messages.asset_ropa.filters[value === 'true' ? 'yes' : 'no'];

export const buildAssetRopaReviewAvailableFilters = (
  filterOptions: AssetRopaReviewFilterOptions,
): AssetRopaReviewTableAvailableFilters => ({
  review_status: {
    sectionName: processing_activities_messages.asset_ropa.filters.review_status,
    filters: REVIEW_STATUS_OPTIONS.map(value => ({
      id: value,
      name: reviewStatusMessage(value),
    })),
    isSingleSelect: true,
  },
  ...(filterOptions.assetTypes.length > 0
    ? {
        asset_type: {
          sectionName: processing_activities_messages.asset_ropa.filters.asset_type,
          filters: filterOptions.assetTypes.map(assetType => ({
            id: String(assetType.id),
            name: assetType.name,
          })),
          isSingleSelect: true,
        },
      }
    : {}),
  ...(filterOptions.operationalOwners.length > 0
    ? {
        operational_owner: {
          sectionName: processing_activities_messages.asset_ropa.filters.operational_owner,
          filters: filterOptions.operationalOwners.map(owner => ({
            id: String(owner.id),
            name: owner.name,
          })),
          isSingleSelect: true,
        },
      }
    : {}),
  warning_state: {
    sectionName: processing_activities_messages.asset_ropa.filters.warning_state,
    filters: WARNING_STATE_OPTIONS.map(value => ({
      id: value,
      name: warningStateMessage(value),
    })),
    isSingleSelect: true,
  },
  dpia_indication: buildDpiaIndicationDrawerSection(),
  dpia_status: buildDpiaCombinedStatusDrawerSection(),
  sensitive_personal_data: {
    sectionName: processing_activities_messages.asset_ropa.filters.sensitive_personal_data,
    filters: BOOLEAN_FILTER_OPTIONS.map(value => ({
      id: value,
      name: booleanFilterMessage(value),
    })),
    isSingleSelect: true,
  },
  third_country_processing: {
    sectionName: processing_activities_messages.asset_ropa.filters.third_country_processing,
    filters: BOOLEAN_FILTER_OPTIONS.map(value => ({
      id: value,
      name: booleanFilterMessage(value),
    })),
    isSingleSelect: true,
  },
});

export const assetRopaReviewFilterValuesToTableFilters = (
  filters: AssetRopaReviewFilters,
  filterOptions: AssetRopaReviewFilterOptions = { assetTypes: [], operationalOwners: [] },
): AssetRopaReviewTableFilterState => {
  const tableFilters: AssetRopaReviewTableFilterState = {
    ...writeDpiaFiltersToTableSections(filters),
  };

  if (filters.reviewStatus) {
    tableFilters.review_status = [{ id: filters.reviewStatus, name: reviewStatusMessage(filters.reviewStatus) }];
  }

  if (filters.assetTypeId) {
    const assetTypeName = filterOptions.assetTypes.find(assetType => assetType.id === filters.assetTypeId)?.name;
    tableFilters.asset_type = [{ id: String(filters.assetTypeId), name: assetTypeName ?? String(filters.assetTypeId) }];
  }

  if (filters.operationalOwnerMembershipId) {
    const ownerName = filterOptions.operationalOwners.find(owner => owner.id === filters.operationalOwnerMembershipId)?.name;
    tableFilters.operational_owner = [
      { id: String(filters.operationalOwnerMembershipId), name: ownerName ?? String(filters.operationalOwnerMembershipId) },
    ];
  }

  if (filters.warningState) {
    tableFilters.warning_state = [{ id: filters.warningState, name: warningStateMessage(filters.warningState) }];
  }

  if (filters.sensitivePersonalData) {
    tableFilters.sensitive_personal_data = [
      { id: filters.sensitivePersonalData, name: booleanFilterMessage(filters.sensitivePersonalData) },
    ];
  }

  if (filters.thirdCountryProcessing) {
    tableFilters.third_country_processing = [
      { id: filters.thirdCountryProcessing, name: booleanFilterMessage(filters.thirdCountryProcessing) },
    ];
  }

  return tableFilters;
};

export const mergeTableFiltersIntoAssetRopaReviewFilters = (
  filters: AssetRopaReviewFilters,
  tableFilters: AssetRopaReviewTableFilterState,
): AssetRopaReviewFilters => {
  if (Object.keys(tableFilters).length === 0) {
    return {
      ...DEFAULT_ASSET_ROPA_REVIEW_FILTERS,
      search: filters.search,
    };
  }

  const assetTypeIdRaw = tableFilters.asset_type?.[0]?.id;
  const parsedAssetTypeId = assetTypeIdRaw ? Number(assetTypeIdRaw) : NaN;
  const operationalOwnerIdRaw = tableFilters.operational_owner?.[0]?.id;
  const parsedOperationalOwnerId = operationalOwnerIdRaw ? Number(operationalOwnerIdRaw) : NaN;
  const dpiaFilters = readDpiaFiltersFromTableSections(tableFilters);

  return {
    ...filters,
    reviewStatus: (tableFilters.review_status?.[0]?.id as AssetRopaReviewFilters['reviewStatus']) ?? '',
    assetTypeId: Number.isFinite(parsedAssetTypeId) ? parsedAssetTypeId : '',
    operationalOwnerMembershipId: Number.isFinite(parsedOperationalOwnerId) ? parsedOperationalOwnerId : '',
    warningState: (tableFilters.warning_state?.[0]?.id as AssetRopaReviewFilters['warningState']) ?? '',
    ...dpiaFilters,
    sensitivePersonalData: (tableFilters.sensitive_personal_data?.[0]?.id as AssetRopaReviewFilters['sensitivePersonalData']) ?? '',
    thirdCountryProcessing: (tableFilters.third_country_processing?.[0]?.id as AssetRopaReviewFilters['thirdCountryProcessing']) ?? '',
  };
};
